import { loadGoogleAnalytics } from './loadGoogleAnalytics';
import {
  getLinkDomain,
  getPageAnalyticsContext,
  isExternalHref,
  isMailtoOrTelHref,
  normalizeInternalPath,
} from './outboundLink';

/**
 * Intentional GA4 events (keep this set small):
 * - page_view
 * - hero_chat_send
 * - hero_chat_contact_shared
 * - cta_click
 * - case_study_click
 * - work_show_more
 * - hero_chat_reset
 * - outbound_link_click
 * - internal_link_click
 *
 * Register event params in GA4 Admin – see ga4Dimensions.js
 */

const canTrack = () =>
  typeof window !== 'undefined' && process.env.NODE_ENV === 'production';

const MAX_CHAT_QUESTION_LENGTH = 200;

export const sanitizeChatQuestion = (text = '') => {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  if (normalized.length <= MAX_CHAT_QUESTION_LENGTH) return normalized;
  return `${normalized.slice(0, MAX_CHAT_QUESTION_LENGTH)}…`;
};

const sendEvent = (eventName, params) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, {
      ...getPageAnalyticsContext(),
      ...params,
    });
  }
};

export const trackEvent = (eventName, params = {}) => {
  if (!canTrack()) return;

  if (typeof window.gtag === 'function') {
    sendEvent(eventName, params);
    return;
  }

  loadGoogleAnalytics().then(() => sendEvent(eventName, params));
};

export const resolveCtaName = (href = '', label = '') => {
  const h = href.toLowerCase();
  const l = label.toLowerCase();

  if (h.includes('linkedin.com')) return 'linkedin';
  if (h.startsWith('mailto:')) return 'email';
  if (h.includes('github.com')) return 'github';
  if (h.includes('drive.google') || l.includes('resume')) return 'resume';
  if (l.includes('calendar') || l.includes('schedule')) return 'schedule';
  return 'external';
};

export const trackCtaClick = (href, label, extra = {}) => {
  trackEvent('cta_click', {
    cta_name: resolveCtaName(href, label),
    link_url: href,
    link_text: label,
    ...(isMailtoOrTelHref(href)
      ? { link_type: href.toLowerCase().startsWith('tel:') ? 'tel' : 'mailto' }
      : {}),
    ...extra,
  });
};

export const trackOutboundLinkClick = (href, linkText = '', extra = {}) => {
  if (!isExternalHref(href)) return;

  trackEvent('outbound_link_click', {
    link_url: href,
    link_text: linkText,
    link_domain: getLinkDomain(href),
    cta_name: resolveCtaName(href, linkText),
    link_type: 'http',
    outbound: true,
    transport_type: 'beacon',
    ...extra,
  });
};

export const trackInternalLinkClick = (href, linkText = '', extra = {}) => {
  trackEvent('internal_link_click', {
    link_url: href,
    link_text: linkText,
    link_path: normalizeInternalPath(href),
    ...extra,
  });
};
